// frontend/src/pages/KeyRotationPage.jsx
import React, { useState, useCallback } from 'react';
import { scanEncryptedRows, listKeyVersions, generateNewKey, rotateKey } from '../api/keyRotation.js';
import { formatDateTime } from '../utils/dateTime';

const CONFIRM_PHRASE = 'CONFIRM ROTATION';

// ── Small reusable components ────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color }) => (
  <div className={`rounded-2xl p-5 text-white shadow-lg ${color}`}>
    <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">{label}</p>
    <p className="text-3xl font-bold">{value}</p>
    {sub && <p className="text-xs opacity-60 mt-1">{sub}</p>}
  </div>
);

const Badge = ({ ok }) => ok
  ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">✓ OK</span>
  : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">✗ Failed</span>;

const Spinner = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

// ── Masked key display with copy + download ──────────────────────────────────
const KeyDisplay = ({ value, label }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* ignore */ }
  };

  const handleDownload = () => {
    const ts = new Date().toISOString().slice(0, 10);
    const content = `AIDAS Platform — Fernet Key\nGenerated: ${new Date().toISOString()}\nLabel: ${label || 'N/A'}\n\nFERNET_KEY=${value}\n\nSTORE THIS FILE SECURELY AND DELETE AFTER UPDATING YOUR .env FILE.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `fernet-key-${ts}.txt`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const masked = '•'.repeat(44);
  return (
    <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
      <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">New Fernet Key</p>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="flex-1 font-mono text-sm bg-white border border-indigo-100 rounded-lg px-3 py-2 break-all text-gray-800 select-all">
          {revealed ? value : masked}
        </code>
        <button onClick={() => setRevealed(r => !r)}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition whitespace-nowrap">
          {revealed ? '🙈 Hide' : '👁 Reveal'}
        </button>
        <button onClick={handleCopy}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition whitespace-nowrap">
          {copied ? '✓ Copied!' : '📋 Copy'}
        </button>
        <button onClick={handleDownload}
          className="px-3 py-2 rounded-lg text-xs font-semibold bg-violet-100 text-violet-700 hover:bg-violet-200 transition whitespace-nowrap">
          ⬇ Download
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        💡 The new key is automatically active. Existing data decrypts transparently via the key history table. Optionally update <code className="bg-gray-100 px-1 rounded">FERNET_KEY</code> in your <code className="bg-gray-100 px-1 rounded">.env</code> for consistency — no restart required.
      </p>
    </div>
  );
};

// ── Scan results panel ───────────────────────────────────────────────────────
const ScanPanel = ({ scan }) => {
  if (!scan) return null;
  const tables = [
    { label: 'Integration Keys', data: scan.api_keys },
    { label: 'Data Sources (data_sources)', data: scan.data_sources },
  ];
  return (
    <div className="mt-6 rounded-2xl border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <span className="font-semibold text-sm text-gray-700">Scan Results</span>
        <Badge ok={scan.all_keys_ok} />
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            {['Table', 'Total', 'Decryptable', 'Failed', 'Status'].map(h => (
              <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tables.map(({ label, data }) => (
            <tr key={label} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-800">{label}</td>
              <td className="px-4 py-3 text-gray-600">{data?.total ?? 0}</td>
              <td className="px-4 py-3 text-emerald-600 font-semibold">{data?.ok ?? 0}</td>
              <td className="px-4 py-3 text-rose-600 font-semibold">{data?.failed ?? 0}</td>
              <td className="px-4 py-3"><Badge ok={(data?.failed ?? 0) === 0} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Key version history table ────────────────────────────────────────────────
const KeyHistory = ({ keys }) => {
  if (!keys?.length) return null;
  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
        <span className="font-semibold text-sm text-gray-700">Key Version History</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            {['Version', 'Label', 'Status', 'Rotated By', 'Date'].map(h => (
              <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {keys.map(k => (
            <tr key={k.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-bold text-indigo-700">v{k.version}</td>
              <td className="px-4 py-3 text-gray-700">{k.label || '—'}</td>
              <td className="px-4 py-3">
                {k.is_active
                  ? <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">Active</span>
                  : <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full">Retired</span>}
              </td>
              <td className="px-4 py-3 text-gray-500 font-mono text-xs">{k.created_by || '—'}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">
                {k.created_at ? formatDateTime(k.created_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
export default function KeyRotationPage({ token }) {
  const [scan, setScan] = useState(null);
  const [keys, setKeys] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rotating, setRotating] = useState(false);

  const [generatedKey, setGeneratedKey] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [label, setLabel] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [useGenerated, setUseGenerated] = useState(true);

  const [result, setResult] = useState(null);   // rotation success result
  const [error, setError] = useState(null);
  const [successKey, setSuccessKey] = useState(''); // the new key after rotation

  const activeKey = useGenerated ? generatedKey : customKey;
  const canRotate = activeKey.length >= 44 && confirmPhrase === CONFIRM_PHRASE;

  const handleScan = useCallback(async () => {
    setScanning(true); setError(null);
    try { setScan(await scanEncryptedRows(token)); }
    catch (e) { setError(e.message || 'Scan failed'); }
    finally { setScanning(false); }
  }, [token]);

  const handleLoadKeys = useCallback(async () => {
    setLoadingKeys(true); setError(null);
    try { const k = await listKeyVersions(token); setKeys(Array.isArray(k) ? k : []); }
    catch (e) { setError(e.message || 'Failed to load key history'); }
    finally { setLoadingKeys(false); }
  }, [token]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true); setError(null);
    try {
      const r = await generateNewKey(token);
      setGeneratedKey(r.new_key || '');
      setUseGenerated(true);
    } catch (e) { setError(e.message || 'Key generation failed'); }
    finally { setGenerating(false); }
  }, [token]);

  const handleRotate = useCallback(async () => {
    if (!canRotate) return;
    setRotating(true); setError(null); setResult(null);
    try {
      const r = await rotateKey(token, activeKey, label || undefined);
      setSuccessKey(r.new_key);
      setResult(r);
      setGeneratedKey('');
      setCustomKey('');
      setConfirmPhrase('');
      setLabel('');
      // Refresh key history automatically
      const k = await listKeyVersions(token);
      setKeys(Array.isArray(k) ? k : []);
    } catch (e) { setError(e.message || 'Rotation failed'); }
    finally { setRotating(false); }
  }, [token, canRotate, activeKey, label]);

  return (
    <div className="max-w-5xl mx-auto space-y-8">

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-600 to-violet-700 flex items-center justify-center shadow-lg">
          <span className="text-2xl">🔐</span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fernet Key Rotation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Super Admin — Manage platform encryption key lifecycle</p>
        </div>
      </div>

      {/* Warning banner */}
      <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-5 flex gap-4">
        <span className="text-2xl">⚠️</span>
        <div className="text-sm text-rose-800 space-y-1">
          <p className="font-bold text-base">Critical Security Operation</p>
          <p>Rotating the Fernet key re-encrypts <strong>all</strong> sensitive data in the platform (integration keys, data source passwords). All historical keys are retained in the database for transparent backward compatibility.</p>
          <p className="font-semibold">The platform continues working immediately after rotation — no restart required. Optionally update <code className="bg-rose-100 rounded px-1">FERNET_KEY</code> in your <code className="bg-rose-100 rounded px-1">.env</code> file for long-term consistency.</p>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm font-medium">
          ✗ {error}
        </div>
      )}

      {/* Success result */}
      {result && (
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 space-y-4">
          <p className="text-emerald-800 font-bold text-lg flex items-center gap-2">✅ Rotation Complete — v{result.new_version}</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-xl p-4 border border-emerald-100">
              <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Integration Keys Rotated</p>
              <p className="text-2xl font-bold text-emerald-700">{result.stats?.api_keys?.rotated ?? 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-emerald-100">
              <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Data Sources Rotated</p>
              <p className="text-2xl font-bold text-emerald-700">{result.stats?.data_sources?.rotated ?? 0}</p>
            </div>
          </div>
          <KeyDisplay value={successKey} label={result.label} />
        </div>
      )}

      {/* Scan section */}
      <div className="rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-800 dark:text-white">Step 1 — Scan Encrypted Data</h2>
            <p className="text-sm text-gray-500 mt-0.5">Dry-run verification. No data is modified.</p>
          </div>
          <button onClick={handleScan} disabled={scanning}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition shadow">
            {scanning ? <><Spinner /> Scanning…</> : '🔍 Run Scan'}
          </button>
        </div>
        <ScanPanel scan={scan} />
      </div>

      {/* Generate / Enter key */}
      <div className="rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 shadow-sm p-6 space-y-4">
        <div>
          <h2 className="font-bold text-gray-800 dark:text-white">Step 2 — Prepare New Key</h2>
          <p className="text-sm text-gray-500 mt-0.5">Generate a cryptographically secure key or paste your own.</p>
        </div>

        <div className="flex gap-3">
          <button onClick={() => setUseGenerated(true)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition ${useGenerated ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            Generate automatically
          </button>
          <button onClick={() => setUseGenerated(false)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition ${!useGenerated ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            Enter manually
          </button>
        </div>

        {useGenerated ? (
          <div className="space-y-3">
            <button onClick={handleGenerate} disabled={generating}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-60 transition shadow">
              {generating ? <><Spinner /> Generating…</> : '⚡ Generate New Key'}
            </button>
            {generatedKey && (
              <div className="rounded-xl bg-violet-50 border border-violet-200 p-4">
                <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-1">Generated Key (preview)</p>
                <code className="font-mono text-xs text-violet-800 break-all">{generatedKey.slice(0, 12)}{'•'.repeat(20)}{generatedKey.slice(-8)}</code>
                <p className="text-xs text-violet-500 mt-1">Full key will be revealed after rotation.</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Paste Fernet Key</label>
            <input
              type="password"
              value={customKey}
              onChange={e => setCustomKey(e.target.value.trim())}
              placeholder="Paste base64-urlsafe Fernet key (44 chars)…"
              className="w-full font-mono text-sm px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            {customKey && customKey.length < 44 && (
              <p className="text-xs text-rose-500 mt-1">Key too short. Fernet keys are 44 characters.</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Label <span className="font-normal text-gray-400">(optional)</span></label>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. 2026-05 quarterly rotation"
            className="w-full text-sm px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      {/* Confirm & Rotate */}
      <div className="rounded-2xl border-2 border-rose-100 bg-white dark:bg-gray-800 shadow-sm p-6 space-y-4">
        <div>
          <h2 className="font-bold text-gray-800 dark:text-white">Step 3 — Confirm &amp; Rotate</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Type <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-rose-600 text-xs">{CONFIRM_PHRASE}</code> to enable the rotate button.
          </p>
        </div>

        <input
          value={confirmPhrase}
          onChange={e => setConfirmPhrase(e.target.value)}
          placeholder={`Type "${CONFIRM_PHRASE}" to confirm`}
          className="w-full text-sm px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-rose-400 font-mono"
        />

        <button
          onClick={handleRotate}
          disabled={!canRotate || rotating}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-violet-600 text-white font-bold text-sm hover:from-rose-700 hover:to-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-lg shadow-rose-200">
          {rotating ? <><Spinner /> Rotating all encrypted data…</> : '🔄 Rotate Encryption Key'}
        </button>

        {!activeKey && (
          <p className="text-xs text-amber-600">← Complete Step 2 first to prepare a new key.</p>
        )}
      </div>

      {/* Key history */}
      <div className="rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-800 dark:text-white">Key Version History</h2>
            <p className="text-sm text-gray-500 mt-0.5">All keys are retained for multi-key fallback decryption.</p>
          </div>
          <button onClick={handleLoadKeys} disabled={loadingKeys}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-60 transition">
            {loadingKeys ? <><Spinner /> Loading…</> : '↻ Load History'}
          </button>
        </div>
        <KeyHistory keys={keys} />
        {!keys.length && !loadingKeys && (
          <p className="text-sm text-gray-400 text-center py-4">Click "Load History" to view key versions.</p>
        )}
      </div>
    </div>
  );
}
